import { useEffect, useRef, useState } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import type { PhotoType } from "@/components/photos-list";
import { Button } from "@/components/ui/button";
import { DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { CloseIcon, LeftArrowIcon, LeftChevronIcon } from "./icons";

const NAV_BUTTON_CLASS_NAME = cn(
  "absolute top-1/2 hidden size-12.5 -translate-y-1/2 rounded-full bg-white transition-colors md:flex",
  "*:text-bg-rose-red *:transition-colors",
  "hover:bg-rose-red hover:*:text-white"
);

interface PhotoProps {
  onChange(index: number): void;
  onClose(): void;
  photo: number;
  photos: PhotoType[];
}

export default function Photo({
  onChange,
  onClose,
  photo,
  photos,
}: PhotoProps) {
  const [photoIndex, setPhotoIndex] = useState(photo);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const scrollingTo = useRef(-1);

  const updatePhotoIndex = (
    index: number,
    behavior: ScrollBehavior = "smooth"
  ) => {
    setPhotoIndex(index);
    scrollingTo.current = index;
    imageContainerRef.current?.children.item(index)?.scrollIntoView({
      behavior,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: its fine
  useEffect(() => {
    setTimeout(() => {
      updatePhotoIndex(photo, "instant" as ScrollBehavior);
    }, 100);
  }, [photo]);

  useEffect(() => {
    const { current } = imageContainerRef;

    if (!current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const activated = entries.reduce((max, entry) =>
          entry.intersectionRatio > max.intersectionRatio ? entry : max
        );

        if (activated.intersectionRatio > 0) {
          const newIndex = Number.parseInt(
            activated.target.getAttribute("id") || "0",
            10
          );
          if (scrollingTo.current === -1) {
            setPhotoIndex(newIndex);
          } else if (scrollingTo.current === newIndex) {
            scrollingTo.current = -1;
          }
        }
      },
      {
        root: current,
        threshold: 0.5,
      }
    );

    for (let i = 0; i < current.children.length; i++) {
      current.children[i].setAttribute("id", i.toString());
      observer.observe(current.children[i]);
    }

    return () => observer.disconnect();
  }, []);

  const handleClick =
    (index: number, skipChecks = false) =>
    () => {
      let idx = index;
      if (!skipChecks) {
        if (index < 0) {
          idx = photos.length - 1;
        }
        if (index >= photos.length) {
          idx = 0;
        }
      }

      if (idx === -1) {
        onChange(idx);
      } else {
        updatePhotoIndex(idx);
      }
    };

  return (
    <>
      <DialogHeader className="flex-row items-center justify-between px-6 py-5 md:px-10 md:py-8">
        <Button
          className="flex h-7 rounded-[14px] border border-white/50 px-4 font-semibold text-12 text-white md:h-[45px] md:rounded-[23px] md:text-14"
          onClick={handleClick(-1, true)}
        >
          <LeftArrowIcon />
          Gallery
        </Button>
        <Button
          aria-label="Close"
          className="flex size-6 rounded-full border border-white/50 md:size-7.5"
          onClick={onClose}
        >
          <CloseIcon />
        </Button>
      </DialogHeader>
      <div className="relative grow-1 items-center justify-center">
        <div className="scrollbar-hidden flex size-full snap-x snap-mandatory overflow-auto *:snap-center md:hidden">
          {photos.map(
            ({ imageId }) =>
              !!imageId && (
                <CloudinaryImage
                  className="aspect-[375/281] w-full shrink-0 object-contain"
                  imageId={imageId}
                  key={imageId}
                />
              )
          )}
        </div>
        <div className="hidden h-[calc(100dvh-108px-88px)] px-32.5 md:block">
          <CloudinaryImage
            className="size-full object-contain"
            imageId={photos[photoIndex].imageId || ""}
          />
        </div>
        {photos.length > 1 && (
          <>
            <Button
              aria-label="Previous"
              className={cn(NAV_BUTTON_CLASS_NAME, "left-10")}
              onClick={handleClick(photoIndex - 1)}
            >
              <LeftChevronIcon />
            </Button>
            <Button
              aria-label="Next"
              className={cn(NAV_BUTTON_CLASS_NAME, "right-10 rotate-180")}
              onClick={handleClick(photoIndex + 1)}
            >
              <LeftChevronIcon />
            </Button>
          </>
        )}
      </div>
      <DialogFooter className="flex-row justify-center gap-2 p-10 sm:justify-center">
        {photos.map(({ imageId }, index) => (
          <Button
            aria-label={`Photo #${index + 1}`}
            className={cn(
              "size-2",
              index === photoIndex ? "bg-rose-red" : "bg-white/40"
            )}
            key={imageId}
            onClick={handleClick(index)}
          />
        ))}
      </DialogFooter>
    </>
  );
}
